/**
 * CastProfilePicker Component
 * Allows users to select a profile thumbnail from LoRA training images
 * or checkpoint inference samples.
 */

import { useState } from "react";
import { Camera, Check, Loader2, ImageIcon, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAssetUrl } from "@/hooks/loras/useAssetUrl";
import { useDatasetPrep } from "@/hooks/loras/useDatasetPrep";
import { useLoraCheckpoints } from "@/hooks/loras/useLoraCheckpoints";
import { useUpdateLora } from "@/hooks/loras/useLoraMutations";
import { cn } from "@/lib/utils";

interface CastProfilePickerProps {
  loraId: string;
  currentThumbnailAssetId?: string | null;
  /** Callback when thumbnail is successfully updated */
  onUpdate?: () => void;
}

interface ImageOption {
  assetId: string;
  source: "training" | "checkpoint";
  label: string;
}

function ThumbnailImage({
  assetId,
  alt,
  selected,
  onClick,
}: {
  assetId: string;
  alt: string;
  selected: boolean;
  onClick: () => void;
}) {
  const { data: url, isLoading } = useAssetUrl(assetId);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative aspect-square rounded-lg overflow-hidden border-2 transition-all",
        selected
          ? "border-primary ring-2 ring-primary/30"
          : "border-transparent hover:border-white/30",
        "bg-muted",
      )}
    >
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      ) : url ? (
        <img
          src={url}
          alt={alt}
          className="w-full h-full object-cover rounded-lg"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="w-6 h-6 text-muted-foreground" />
        </div>
      )}
      {selected && (
        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
          <Check className="w-3 h-3 text-primary-foreground" />
        </div>
      )}
    </button>
  );
}

export function CastProfilePicker({
  loraId,
  currentThumbnailAssetId,
  onUpdate,
}: CastProfilePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(
    currentThumbnailAssetId ?? null,
  );

  // Fetch training images from dataset prep (only when dialog is open)
  const { data: datasetPrep } = useDatasetPrep(
    isOpen ? loraId : undefined,
    isOpen,
  );

  // Fetch checkpoint sample images (only when dialog is open)
  const { data: checkpoints } = useLoraCheckpoints(isOpen ? loraId : null);

  // Update mutation
  const updateLora = useUpdateLora(loraId);

  // Build list of available images
  const imageOptions: ImageOption[] = [];

  // Add training reference images
  if (datasetPrep?.captions) {
    datasetPrep.captions.forEach((caption, index) => {
      imageOptions.push({
        assetId: caption.assetId,
        source: "training",
        label: `Training image ${index + 1}`,
      });
    });
  }

  // Add checkpoint sample images
  if (checkpoints) {
    checkpoints.forEach((checkpoint) => {
      checkpoint.sampleInferenceAssetIds?.forEach((assetId, index) => {
        imageOptions.push({
          assetId,
          source: "checkpoint",
          label: `Step ${checkpoint.stepNumber} sample ${index + 1}`,
        });
      });
    });
  }

  const handleSave = async () => {
    if (!selectedAssetId) return;

    try {
      await updateLora.mutateAsync({ coverAssetId: selectedAssetId });
      onUpdate?.();
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to update thumbnail:", error);
    }
  };

  const handleOpen = () => {
    setSelectedAssetId(currentThumbnailAssetId ?? null);
    setIsOpen(true);
  };

  const trainingImages = imageOptions.filter((o) => o.source === "training");
  const checkpointImages = imageOptions.filter(
    (o) => o.source === "checkpoint",
  );

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpen}
        className="gap-2"
      >
        <Camera className="w-4 h-4" />
        Change Thumbnail
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b -mx-6 px-6">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Choose Profile Thumbnail
            </DialogTitle>
            <DialogDescription>
              Select an image from your training uploads or checkpoint samples
              to use as this character&apos;s profile picture.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4 space-y-6">
            {/* Training Images Section */}
            {trainingImages.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Training Reference Images ({trainingImages.length})
                </h3>
                <div className="grid grid-cols-5 gap-2">
                  {trainingImages.map((option) => (
                    <ThumbnailImage
                      key={option.assetId}
                      assetId={option.assetId}
                      alt={option.label}
                      selected={selectedAssetId === option.assetId}
                      onClick={() => setSelectedAssetId(option.assetId)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Checkpoint Samples Section */}
            {checkpointImages.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  AI Generated Samples ({checkpointImages.length})
                </h3>
                <div className="grid grid-cols-5 gap-2">
                  {checkpointImages.map((option) => (
                    <ThumbnailImage
                      key={option.assetId}
                      assetId={option.assetId}
                      alt={option.label}
                      selected={selectedAssetId === option.assetId}
                      onClick={() => setSelectedAssetId(option.assetId)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {imageOptions.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No images available yet.</p>
                <p className="text-sm mt-1">
                  Upload training images or complete a training run to select a
                  thumbnail.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4 border-t -mx-6 px-6">
            <Button variant="ghost" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!selectedAssetId || updateLora.isPending}
            >
              {updateLora.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Thumbnail"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
